import React, { useState } from 'react';

import { Avatar, Box, Button, Flex, Paper, Tabs, Title, useMantineTheme } from '@mantine/core';
import { IconClipboardCheck, IconListTree, IconMessages, IconPhoneIncoming, IconUsers } from '@tabler/icons-react';
import { LanguageChange } from 'components/LanguageChange';
import { TabPanelWrapper } from 'components/TabPanelWrapper';
import { AgentIcon } from 'icons/AgentIcon';
import { OverviewTab } from 'pages/GuestSmartAssistant/GuestDashboard/OverviewTab/OverviewTab';
import { ConversationsSection } from 'pages/ManageSmartAssistant/Sections/ConversationSections/ConversationsSection/ConversationsSection';
import { VoiceCallsSection } from 'pages/ManageSmartAssistant/Sections/ConversationSections/VoiceCallsSection/VoiceCallsSection';
import { EvaluationsSection } from 'pages/ManageSmartAssistant/Sections/EvaluationsSection/EvaluationsSection';
import { LeadsAnalyticsSection } from 'pages/ManageSmartAssistant/Sections/LeadsAnalyticsSection';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { GUEST_DASHBOARD_TABS, ROUTE_LINKS } from 'shared/constants/route-links';
import { GUEST_SMART_ASSISTANT_DASHBOARD_TABS_BY_TYPE } from 'shared/constants/smart-assistant';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

type GuestSmartAssistantDashboardTab = {
  value: string;
  component: React.ReactNode;
  icon: React.ReactNode;
  label: string;
};

interface GuestDashboardProps {
  smartAssistant: SmartAssistant;
  avatarUrl?: string;
}

export function GuestDashboard({ smartAssistant, avatarUrl }: GuestDashboardProps) {
  const { smartAssistantId } = useParams();
  const { t } = useTranslation();
  const { tab } = useParams();
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>(tab || GUEST_DASHBOARD_TABS.OVERVIEW);

  const GUEST_DASHBOARD_TABS_MAP: Record<string, GuestSmartAssistantDashboardTab> = {
    [GUEST_DASHBOARD_TABS.OVERVIEW]: {
      value: GUEST_DASHBOARD_TABS.OVERVIEW,
      component: <OverviewTab smartAssistant={smartAssistant} />,
      icon: <IconListTree />,
      label: t('SMART_ASSISTANT.OVERVIEW_SECTION.TITLE')
    },
    [GUEST_DASHBOARD_TABS.CONVERSATIONS]: {
      value: GUEST_DASHBOARD_TABS.CONVERSATIONS,
      component: <ConversationsSection smartAssistant={smartAssistant} isGuest />,
      icon: <IconMessages />,
      label: t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TITLE')
    },
    [GUEST_DASHBOARD_TABS.LEADS]: {
      value: GUEST_DASHBOARD_TABS.LEADS,
      component: <LeadsAnalyticsSection smartAssistant={smartAssistant} isGuest />,
      icon: <IconUsers />,
      label: t('SMART_ASSISTANT.LEADS_SECTION.TITLE')
    },
    [GUEST_DASHBOARD_TABS.VOICE_CALLS]: {
      value: GUEST_DASHBOARD_TABS.VOICE_CALLS,
      component: <VoiceCallsSection smartAssistant={smartAssistant} isGuest />,
      icon: <IconPhoneIncoming />,
      label: t('SMART_ASSISTANT.VOICE_CALLS_SECTION.TITLE')
    },
    [GUEST_DASHBOARD_TABS.EVALUATIONS]: {
      value: GUEST_DASHBOARD_TABS.EVALUATIONS,
      component: <EvaluationsSection smartAssistant={smartAssistant} isGuest />,
      icon: <IconClipboardCheck />,
      label: t('SMART_ASSISTANT.EVALUATIONS_SECTION.TITLE')
    }
  };

  const tabs = GUEST_SMART_ASSISTANT_DASHBOARD_TABS_BY_TYPE[smartAssistant.type]
    .map((tab) => GUEST_DASHBOARD_TABS_MAP[tab])
    .filter((tab) => tab !== undefined);

  return (
    <Box h="100svh" w="100svw" p={100} bg="gray.1">
      <Paper
        h="100%"
        w="100%"
        withBorder
        style={{
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Flex direction="row" justify="space-between" align="center" px="md">
          <Flex direction="row" align="center" gap="xs" mih={70}>
            <Avatar size={40} radius={20} src={avatarUrl} />
            <Title order={1} truncate maw="50vw">
              {smartAssistant.name}
            </Title>
          </Flex>
          <Button
            leftIcon={<AgentIcon w={20} h={20} />}
            onClick={() => window.open(`${ROUTE_LINKS.guestSmartAssistant}/${smartAssistantId}`, '_blank')}>
            {t('APP.ACTIONS.TRY_SMART_ASSISTANT')}
          </Button>
        </Flex>
        <Tabs
          value={activeTab}
          orientation="vertical"
          onTabChange={(tabPath) => {
            const newTab = tabPath?.toString() ?? GUEST_DASHBOARD_TABS.OVERVIEW;
            setActiveTab(newTab);
            navigate(`${ROUTE_LINKS.guestSmartAssistantDashboard}/${smartAssistantId}/${newTab}`);
          }}
          styles={{
            root: {
              flexDirection: 'row',
              borderTop: `1px solid ${theme.other.gray.border}`,
              flex: 1,
              minHeight: 0
            },
            tabsList: {
              marginTop: 10,
              marginBottom: 10,
              borderTop: 'none',
              borderRight: `1px solid ${theme.other.gray.border}`,
              minWidth: 200
            },
            tab: {
              borderRight: `1px solid ${theme.other.gray.border}`
            }
          }}>
          <Tabs.List>
            {tabs.map((tab, index: number) => {
              return (
                <Tabs.Tab value={tab.value} icon={tab.icon} key={index}>
                  {tab.label}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
          {tabs.map((tab, index: number) => {
            return (
              <Tabs.Panel key={index} value={tab.value}>
                <TabPanelWrapper p="md">{tab.component}</TabPanelWrapper>
              </Tabs.Panel>
            );
          })}
        </Tabs>
      </Paper>
      <Box style={{ position: 'absolute', left: 20, bottom: 20 }}>
        <LanguageChange />
      </Box>
    </Box>
  );
}
